import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  Button,
  DataTable,
  FeedbackState,
  localTablePage,
  Mono,
  Progress,
  Segmented,
  StatusBadge,
  TableIdentity,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { LiveReload } from "@repo/shared";

import { KpiCell, KpiStrip } from "@/components/app-analysis/kpi";
import { DensityToggle } from "@/components/density-toggle";
import { supportCode } from "@/components/error-screen";
import { isQlikForbidden, liveReloadsQueryOptions } from "@/features/reloads/api";
import { useLocale } from "@/hooks/use-locale";
import { formatSeconds } from "@/lib/duration-format";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { appLabel, reloadOriginLabel, reloadStatusLabel, reloadStatusTone } from "./labels";
import { ViewRunsButton } from "./AppRunsDrawer";
import { QlikAppLink } from "./QlikAppLink";

const PAGE_SIZE = 50;

type NowStatus = "running" | "queued";
type NowFilters = { status?: NowStatus };

// The live endpoint lists only QUEUED, RELOADING and CANCELING.
const isQueued = (row: LiveReload) => row.status === "QUEUED";

const secondsSince = (iso: string, now: number) => Math.max(0, (now - Date.parse(iso)) / 1000);

// Running past the median of the app's succeeded reloads.
const isAboveExpected = (row: LiveReload, now: number) =>
  !isQueued(row) &&
  !!row.expectedSeconds &&
  !!row.startTime &&
  secondsSince(row.startTime, now) > row.expectedSeconds;

export function NowView({
  connectionId,
  baseUrl,
  origin,
  onViewRuns,
}: {
  connectionId: string;
  baseUrl: string;
  origin?: string;
  onViewRuns: (appId: string) => void;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { language } = useLocale();
  const { density, setDensity } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const query = useQuery(liveReloadsQueryOptions(connectionId));

  // Elapsed times move every second between the 15 s polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const [state, setState] = useState<DataTableState<NowFilters>>({
    page: 1,
    pageSize: PAGE_SIZE,
    query: "",
    filters: {},
    selection: [],
  });

  const live = query.data?.data ?? [];
  const reloads = origin ? live.filter((row) => row.type === origin) : live;
  const queued = reloads.filter(isQueued);
  const running = reloads.length - queued.length;
  const above = reloads.filter((row) => isAboveExpected(row, now)).length;
  const longestWait = Math.max(0, ...queued.map((row) => secondsSince(row.creationTime, now)));

  const count = (value: number) => (query.data ? value.toLocaleString(language) : "—");
  const clock = (iso: string) => format(new Date(iso), "HH:mm:ss");

  const columns: DataTableColumn<LiveReload>[] = [
    {
      id: "app",
      header: t("now.columns.app"),
      variant: "identity",
      rowHeader: true,
      cell: (row) => <TableIdentity name={<QlikAppLink app={row} baseUrl={baseUrl} />} />,
    },
    {
      id: "status",
      header: t("now.columns.status"),
      width: "9rem",
      cell: (row) => (
        <StatusBadge tone={reloadStatusTone(row.status)}>
          {reloadStatusLabel(t, row.status)}
        </StatusBadge>
      ),
    },
    {
      id: "origin",
      header: t("now.columns.origin"),
      width: "10rem",
      cell: (row) => reloadOriginLabel(t, row.type),
    },
    {
      id: "start",
      header: t("now.columns.start"),
      width: "8rem",
      align: "end",
      variant: "numeric",
      cell: (row) => (row.startTime ? clock(row.startTime) : "—"),
    },
    {
      id: "elapsed",
      header: t("now.columns.elapsed"),
      width: "9rem",
      align: "end",
      variant: "numeric",
      cell: (row) => {
        if (isQueued(row)) {
          return t("now.inQueue", { wait: formatSeconds(secondsSince(row.creationTime, now)) });
        }
        return row.startTime ? formatSeconds(secondsSince(row.startTime, now)) : "—";
      },
    },
    {
      id: "expected",
      header: t("now.columns.expected"),
      width: "8rem",
      align: "end",
      variant: "numeric",
      cell: (row) => (row.expectedSeconds ? formatSeconds(row.expectedSeconds) : "—"),
    },
    {
      id: "progress",
      header: t("now.columns.progress"),
      width: "15rem",
      cell: (row) => {
        if (isQueued(row)) return <span className="text-xs text-dim">{t("now.waiting")}</span>;
        if (!row.expectedSeconds || !row.startTime) return "—";
        const over = isAboveExpected(row, now);
        const pct = Math.min(
          100,
          Math.round((secondsSince(row.startTime, now) / row.expectedSeconds) * 100),
        );
        return (
          <span className="flex items-center gap-2">
            <Progress
              value={pct}
              tone={over ? "warning" : undefined}
              className="w-28"
              aria-hidden
            />
            <span className="text-xs text-dim">{over ? t("now.aboveExpected") : `${pct}%`}</span>
          </span>
        );
      },
    },
  ];

  const page = localTablePage(reloads, state, {
    columns,
    search: appLabel,
    filter: (row, { status }) => !status || (status === "queued") === isQueued(row),
  });

  const countLabel = (label: string, total: number) => (
    <>
      {label} <Mono>{total.toLocaleString(language)}</Mono>
    </>
  );

  if (isQlikForbidden(query.error)) {
    return (
      <FeedbackState
        state="forbidden"
        title={t("now.forbidden.title")}
        description={t("now.forbidden.description")}
        action={
          <Button variant="outline" loading={query.isFetching} onClick={() => void query.refetch()}>
            {t("now.forbidden.retry")}
          </Button>
        }
      />
    );
  }

  const supportInfo = supportCode(query.error, tc("error.supportCode"));

  return (
    <div className="flex flex-col gap-4">
      <KpiStrip>
        <KpiCell label={t("status.RELOADING")} value={count(running)} />
        <KpiCell
          label={t("status.QUEUED")}
          value={count(queued.length)}
          subMeta={
            queued.length > 0
              ? t("now.kpi.waiting", { wait: formatSeconds(longestWait) })
              : undefined
          }
        />
        <KpiCell label={t("now.kpi.above")} value={count(above)} subMeta={t("now.kpi.aboveHint")} />
        <KpiCell
          label={t("now.kpi.updated")}
          value={query.data ? clock(query.data.fetchedAt) : "—"}
          subMeta={t("now.kpi.updatedHint")}
        />
      </KpiStrip>

      <div className="flex flex-col gap-3">
        <DataTable
          label={t("tabs.now")}
          density={density}
          columns={columns}
          rows={page.rows}
          getRowId={(row) => row.reloadId}
          state={state}
          onStateChange={setState}
          loading={query.isPending}
          refreshing={listRefreshing(query)}
          error={
            query.isError
              ? {
                  title: t("error.load"),
                  description: supportInfo && (
                    <>
                      {supportInfo.label}: <Mono>{supportInfo.value}</Mono>
                    </>
                  ),
                }
              : null
          }
          onRetry={() => void query.refetch()}
          search={{
            ...tableSearch(t("now.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
          }}
          toolbar={
            <>
              <Segmented<"all" | NowStatus>
                size="compact"
                label={t("now.filterLabel")}
                value={state.filters.status ?? "all"}
                onValueChange={(value) =>
                  setState((prev) =>
                    updateTableState(prev, {
                      filters: value === "all" ? {} : { status: value },
                    }),
                  )
                }
                options={[
                  { value: "all", label: countLabel(t("now.filterAll"), reloads.length) },
                  { value: "running", label: countLabel(t("status.RELOADING"), running) },
                  { value: "queued", label: countLabel(t("status.QUEUED"), queued.length) },
                ]}
              />
              <DensityToggle value={density} onChange={setDensity} />
            </>
          }
          rowActions={{
            label: t("actions.column"),
            width: 56,
            render: (row) => <ViewRunsButton app={row} onClick={() => onViewRuns(row.appId)} />,
          }}
          pagination={{ kind: "offset", total: page.total, labels: paginationLabels() }}
          messages={tableMessages({ empty: { title: t("now.empty") } })}
        />
        <p className="text-xs text-faint">{t("now.note")}</p>
      </div>
    </div>
  );
}
